import { useMemo, useState } from 'react';
import styles from './Calculator.module.css';

type ExampleKey = 'yield' | 'protein' | 'height' | 'flowering';

interface GenerationRow {
  generation: number;
  mean: number;
  response: number;
  cumulativeGain: number;
}

interface SimulationResult {
  rows: GenerationRow[];
  responsePerGen: number;
  mean0: number;
  units: string;
}

const EXAMPLES: Record<
  ExampleKey,
  {
    mean0: number;
    std: number;
    h2: number;
    intensity: number;
    generations: number;
    units: string;
    name: string;
  }
> = {
  yield: {
    mean0: 6500,
    std: 1200,
    h2: 0.25,
    intensity: 1.76,
    generations: 10,
    units: 'kg/ha',
    name: 'Rendimiento en Maiz',
  },
  protein: {
    mean0: 40,
    std: 3.5,
    h2: 0.55,
    intensity: 1.4,
    generations: 8,
    units: '%',
    name: 'Contenido Proteico en Soja',
  },
  height: {
    mean0: 95,
    std: 12,
    h2: 0.7,
    intensity: 1.16,
    generations: 6,
    units: 'cm',
    name: 'Altura de Planta en Trigo',
  },
  flowering: {
    mean0: 75,
    std: 8,
    h2: 0.85,
    intensity: 2.06,
    generations: 5,
    units: 'dias',
    name: 'Dias a Floracion en Arroz',
  },
};

const format = (value: number): string => value.toFixed(2);

export const SeleccionSimulator: React.FC = () => {
  const [mean0, setMean0] = useState<string>('');
  const [std, setStd] = useState<string>('');
  const [units, setUnits] = useState<string>('');
  const [h2, setH2] = useState<string>('');
  const [intensity, setIntensity] = useState<string>('');
  const [generations, setGenerations] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<SimulationResult | null>(null);

  const handleSimulate = (): void => {
    const mean0Num = Number(mean0);
    const stdNum = Number(std);
    const h2Num = Number(h2);
    const intensityNum = Number(intensity);
    const generationsNum = Number(generations);

    setError('');
    setMessage('');

    if (
      Number.isNaN(mean0Num) ||
      Number.isNaN(stdNum) ||
      Number.isNaN(h2Num) ||
      Number.isNaN(intensityNum) ||
      Number.isNaN(generationsNum)
    ) {
      setResult(null);
      setError('Complete todos los campos numericos con valores validos.');
      return;
    }
    if (stdNum < 0 || intensityNum < 0 || generationsNum < 1 || generationsNum > 20) {
      setResult(null);
      setError('Use valores no negativos y un rango de generaciones entre 1 y 20.');
      return;
    }
    if (h2Num < 0 || h2Num > 1) {
      setResult(null);
      setError('La heredabilidad (h2) debe estar entre 0 y 1.');
      return;
    }

    const h = Math.sqrt(h2Num);
    const responsePerGen = intensityNum * h * stdNum;
    const rows: GenerationRow[] = [];
    for (let t = 0; t <= generationsNum; t += 1) {
      const meanT = mean0Num + t * responsePerGen;
      rows.push({
        generation: t,
        mean: meanT,
        response: t === 0 ? 0 : responsePerGen,
        cumulativeGain: t * responsePerGen,
      });
    }

    setResult({
      rows,
      responsePerGen,
      mean0: mean0Num,
      units: units.trim(),
    });
  };

  const handleLoadExample = (key: ExampleKey): void => {
    const ex = EXAMPLES[key];
    setMean0(String(ex.mean0));
    setStd(String(ex.std));
    setH2(String(ex.h2));
    setIntensity(String(ex.intensity));
    setGenerations(String(ex.generations));
    setUnits(ex.units);
    setResult(null);
    setError('');
    setMessage(`Ejemplo cargado: ${ex.name}. Presione "Simular Seleccion".`);
  };

  const chart = useMemo(() => {
    if (!result) {
      return null;
    }

    const width = 920;
    const height = 340;
    const padLeft = 56;
    const padRight = 20;
    const padTop = 20;
    const padBottom = 44;
    const innerWidth = width - padLeft - padRight;
    const innerHeight = height - padTop - padBottom;

    const means = result.rows.map((row) => row.mean);
    const minY = Math.min(...means);
    const maxY = Math.max(...means);
    const yRange = maxY - minY || 1;
    const maxX = result.rows[result.rows.length - 1].generation || 1;

    const x = (generation: number): number => padLeft + (generation / maxX) * innerWidth;
    const y = (mean: number): number => padTop + (1 - (mean - minY) / yRange) * innerHeight;

    const path = result.rows
      .map((row, idx) => `${idx === 0 ? 'M' : 'L'} ${x(row.generation)} ${y(row.mean)}`)
      .join(' ');

    const yTicks = Array.from({ length: 6 }, (_, i) => {
      const ratio = i / 5;
      const value = minY + (1 - ratio) * yRange;
      const yPos = padTop + ratio * innerHeight;
      return { value, yPos };
    });

    const step = Math.max(1, Math.ceil(maxX / 10));
    const xTicks: number[] = [];
    for (let i = 0; i <= maxX; i += step) {
      xTicks.push(i);
    }
    if (xTicks[xTicks.length - 1] !== maxX) {
      xTicks.push(maxX);
    }

    return { width, height, padLeft, padRight, padBottom, path, x, y, yTicks, xTicks };
  }, [result]);

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.simulatorBox}>
        <div className={styles.calculatorHeader}>
          <h2>Simulador de Avance Genético</h2>
          <p>Simule la ganancia genetica acumulada en multiples generaciones.</p>
        </div>

        <div className={styles.inputSection}>
          <h3>Parametros de la poblacion base</h3>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="mean0">Media Poblacional Inicial (mu0)</label>
              <input
                id="mean0"
                type="number"
                step="0.1"
                value={mean0}
                onChange={(event) => setMean0(event.target.value)}
                placeholder="Ej: 50"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="std">Desviacion Estandar (sigma)</label>
              <input
                id="std"
                type="number"
                step="0.1"
                min="0"
                value={std}
                onChange={(event) => setStd(event.target.value)}
                placeholder="Ej: 10"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="units">Unidades</label>
              <input
                id="units"
                type="text"
                value={units}
                onChange={(event) => setUnits(event.target.value)}
                placeholder="Ej: kg/ha, cm, %"
              />
            </div>
          </div>

          <h3>Parametros de seleccion</h3>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="h2">Heredabilidad (h2)</label>
              <input
                id="h2"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={h2}
                onChange={(event) => setH2(event.target.value)}
                placeholder="Ej: 0.45"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="intensity">Intensidad de Seleccion (i)</label>
              <input
                id="intensity"
                type="number"
                step="0.01"
                min="0"
                value={intensity}
                onChange={(event) => setIntensity(event.target.value)}
                placeholder="Ej: 1.76"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="generations">Numero de Generaciones</label>
              <input
                id="generations"
                type="number"
                step="1"
                min="1"
                max="20"
                value={generations}
                onChange={(event) => setGenerations(event.target.value)}
                placeholder="Ej: 10"
              />
            </div>
          </div>

          <div className={styles.infoYellow}>
            <strong>Valores tipicos de intensidad (i):</strong> 10%: 1.76 | 20%: 1.40 | 30%: 1.16
            | 50%: 0.80
          </div>
        </div>

        <button type="button" className={styles.calculateButton} onClick={handleSimulate}>
          Simular Seleccion
        </button>

        {error && <div className={styles.errorBox}>{error}</div>}
        {message && <div className={styles.infoBox}>{message}</div>}

        <div className={styles.examplesSection}>
          <h3>Ejemplos rapidos</h3>
          <div className={styles.exampleButtons}>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('yield')}>
              Rendimiento Maiz
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('protein')}>
              Proteina Soja
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('height')}>
              Altura Trigo
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('flowering')}>
              Dias Floracion
            </button>
          </div>
        </div>

        {result && chart && (
          <div className={styles.resultsSection}>
            <div className={styles.summaryCards}>
              <div className={styles.summaryCard}>
                <h4>Media Inicial</h4>
                <div className={styles.value}>
                  {format(result.mean0)} {result.units}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <h4>Media Final</h4>
                <div className={styles.value}>
                  {format(result.rows[result.rows.length - 1].mean)} {result.units}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <h4>Ganancia Total</h4>
                <div className={styles.value}>
                  {format(result.rows[result.rows.length - 1].cumulativeGain)} {result.units}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <h4>Ganancia por Generacion</h4>
                <div className={styles.value}>
                  {format(result.responsePerGen)} {result.units}
                </div>
              </div>
            </div>

            <div className={styles.chartContainer}>
              <h3>Evolucion de la Media Poblacional</h3>
              <div className={styles.svgChartWrapper}>
                <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className={styles.svgChart}>
                  {chart.yTicks.map((tick) => (
                    <g key={`y-${tick.yPos}`}>
                      <line
                        x1={chart.padLeft}
                        y1={tick.yPos}
                        x2={chart.width - chart.padRight}
                        y2={tick.yPos}
                        className={styles.gridLine}
                      />
                      <text x={chart.padLeft - 8} y={tick.yPos + 4} className={styles.axisLabel} textAnchor="end">
                        {tick.value.toFixed(1)}
                      </text>
                    </g>
                  ))}

                  {chart.xTicks.map((tick) => (
                    <g key={`x-${tick}`}>
                      <line
                        x1={chart.x(tick)}
                        y1={chart.height - chart.padBottom}
                        x2={chart.x(tick)}
                        y2={chart.height - chart.padBottom + 5}
                        className={styles.axisTick}
                      />
                      <text
                        x={chart.x(tick)}
                        y={chart.height - chart.padBottom + 20}
                        className={styles.axisLabel}
                        textAnchor="middle"
                      >
                        G{tick}
                      </text>
                    </g>
                  ))}

                  <line
                    x1={chart.padLeft}
                    y1={chart.height - chart.padBottom}
                    x2={chart.width - chart.padRight}
                    y2={chart.height - chart.padBottom}
                    className={styles.axisLine}
                  />
                  <line
                    x1={chart.padLeft}
                    y1={20}
                    x2={chart.padLeft}
                    y2={chart.height - chart.padBottom}
                    className={styles.axisLine}
                  />

                  <path d={chart.path} className={styles.chartPath} />

                  {result.rows.map((row) => (
                    <circle
                      key={`p-${row.generation}`}
                      cx={chart.x(row.generation)}
                      cy={chart.y(row.mean)}
                      r={4}
                      className={styles.chartPoint}
                    />
                  ))}
                </svg>
              </div>
            </div>

            <div className={styles.generationTable}>
              <h3>Tabla de Generaciones</h3>
              <table>
                <thead>
                  <tr>
                    <th>Generacion</th>
                    <th>Media</th>
                    <th>Respuesta (R)</th>
                    <th>Ganancia Acumulada</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.generation}>
                      <td>
                        <strong>G{row.generation}</strong>
                      </td>
                      <td>
                        {format(row.mean)} {result.units}
                      </td>
                      <td>
                        {format(row.response)} {result.units}
                      </td>
                      <td>
                        {format(row.cumulativeGain)} {result.units}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.formulaNote}>
              <h4>Formulas utilizadas</h4>
              <p>
                <strong>Respuesta por generacion:</strong> R = i x h x sigma
              </p>
              <p>
                <strong>Media en generacion t:</strong> mut = mu0 + (t x R)
              </p>
              <p>
                <strong>Ganancia acumulada:</strong> DeltaG = mut - mu0
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
